import React, { useContext, useMemo } from 'react';
import { useUnmount } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { selectedCurrentCustomer } from '@/store/customer';
import doubleArrowImg from '@/assets/image/double-arrow.png';
import { storagePrefixV2 } from '@/constant';
import {
  BodyChange3DContext,
  BODYPAIN_CARD_2_TEXT,
  BODYPAIN_CARD_CONFIG,
  formatHopesText,
  PAIN_LEVEL_2_PLAIN_TEXT,
} from '../utils';
import CustomMarquee from '../custom-marquee';
import { resetBodyPainAdjustStore, setBodyPainShowType, useBodyPainAdjustStore } from './useBodyPainAdjustStore';
import ShowBodyPainShoulder from './show-shoulder';
import ShowBodyPainWaist from './show-waist';

import cx from './index.module.scss';

interface Props {}

const PAINSHOWTYPE_2_COMPONENT: Record<'shoulder' | 'waist', JSX.Element> = {
  shoulder: <ShowBodyPainShoulder />,
  waist: <ShowBodyPainWaist />,
};

const BodyPainAdjust: React.FC<Props> = () => {
  const showType = useBodyPainAdjustStore.use.showType();
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const { bodyChangeInfo, bodyAdjustDescText } = useContext(BodyChange3DContext);

  const formatBodyPainCardConfig = useMemo(
    () =>
      BODYPAIN_CARD_CONFIG?.map(item => ({
        ...item,
        initLevel: bodyChangeInfo?.painReliefList?.find(item2 => item2.problem === item?.name)?.initLevel,
        level: bodyChangeInfo?.painReliefList?.find(item2 => item2.problem === item?.name)?.level,
      })),
    [bodyChangeInfo?.painReliefList]
  );

  useUnmount(() => {
    resetBodyPainAdjustStore();
  });

  return (
    <div className={cx('BodyPainAdjust')}>
      {showType === 'all' && (
        <CustomMarquee
          content={
            <>
              {!!bodyChangeInfo?.painReliefHopes?.length && (
                <>
                  <span>您的诉求是 </span>
                  <span className="highlight">{formatHopesText(bodyChangeInfo?.painReliefHopes)}</span>，
                </>
              )}
              已进行磁震波 {bodyChangeInfo?.shockwaveTreatmentCount} 个疗程，{bodyAdjustDescText}
            </>
          }
        />
      )}
      <div className={cx('main-area')}>
        <AnimatePresence mode="wait">
          {showType === 'all' ? (
            <motion.div key={showType} className={cx('all-part')}>
              <motion.div className={cx('mirror-bg')} exit={{ display: 'none', transition: { duration: 0 } }} />
              <motion.div
                className={cx('img-box')}
                transition={{ duration: 0.8 }}
                initial={{ top: '30px', opacity: 0.2 }}
                animate={{ top: '0', opacity: 1 }}
                exit={{ left: '-150px', opacity: 0.1, transition: { duration: 0.4 } }}
              >
                <img
                  src={
                    currentCustomer?.gender === 'Female'
                      ? storagePrefixV2 + '/ai-consulation/asdqwfwfadqfq-min.png'
                      : storagePrefixV2 + '/ai-consulation/male-model-bg/asgglsdf.png'
                  }
                  alt=""
                />
              </motion.div>
              {formatBodyPainCardConfig.map((item, index) => (
                <motion.div
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  key={item.name}
                  exit={{ display: 'none', transition: { duration: 0 } }}
                  className={cx('card-item')}
                  onClick={() => setBodyPainShowType(item.name as any)}
                >
                  <div className={cx('dot')} style={item.dotConfig}>
                    <div
                      className={cx(
                        'inner-circle',
                        item.initLevel && item.level ? (item.initLevel !== item.level ? 'green' : 'blue') : 'gray'
                      )}
                    />
                  </div>
                  <div className={cx('connection-line')} style={item.connectionLineConfig} />
                  <div className={cx('value-content')} style={item.valueContentConfig}>
                    <div className={cx('value-header')}>
                      <div className={cx('header-left')}>
                        <div>{BODYPAIN_CARD_2_TEXT[item.name]}</div>
                      </div>
                      <Icon name="arrow-right" />
                    </div>
                    <div className={cx('value-footer')}>
                      <div className={cx('footer-left')}>
                        {item.initLevel || item.initLevel === 0 ? (
                          <>
                            <span>最严重</span>
                            <span className={cx('highlight', `level-${item?.initLevel}`)}>
                              {PAIN_LEVEL_2_PLAIN_TEXT[item.initLevel]}
                            </span>
                          </>
                        ) : (
                          <span className={cx('empty')}>暂无</span>
                        )}
                      </div>
                      <div className={cx('footer-middle')}>
                        <img src={doubleArrowImg} alt="" />
                      </div>
                      <div className={cx('footer-right')}>
                        {item.level || item.level === 0 ? (
                          <>
                            <span>目前</span>
                            <span className={cx('highlight', `level-${item?.level}`)}>
                              {PAIN_LEVEL_2_PLAIN_TEXT[item.level]}
                            </span>
                          </>
                        ) : (
                          <span className={cx('empty')}>暂无</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <React.Fragment key={showType}>{PAINSHOWTYPE_2_COMPONENT[showType]}</React.Fragment>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BodyPainAdjust;
